import React, { type ReactNode, useMemo } from 'react';
import { App, ConfigProvider } from 'antd';
import { useIntl } from 'react-intl';
import type { Locale } from 'antd/lib/locale';
import antdLocaleUS from 'antd/locale/en_US';
import { getAntdTheme } from 'constants/antd.constants';
import { observer } from 'mobx-react';
import { useTheme } from 'hooks/use-theme';
import { css, Global } from '@emotion/react';
import { IconProvider } from '@ant-design/icons';
import { Empty, CompactEmpty } from 'components/common/empty';
import { TableEmptyState } from 'components/common/table/table-empty-state';

/**
 * This component is used to wrap the entire application.
 * The ConfigProvider sets the base tokens and the locales (translations) for the antd components.
 * The App component is used to set the base styles for the application (i.e. root font-size, root color, etc.)
 */

/**
 * antd 5.6.4 portals every popup to document.body, so a Select inside a Popover is ranked against it
 * by raw z-index instead of nesting inside it. The DS ladder puts dropdown below drawer and modal,
 * which holds only when a nested popup escapes that order through its container's stacking context.
 * Portalling into the container restores it. antd 5.11's zIndexContext replaces this — APO-462.
 */
const NESTED_POPUP_CONTAINERS = '.ant-modal-content, .ant-drawer-body, .ant-popover-inner';

export const getNestedPopupContainer = (triggerNode?: HTMLElement) =>
  triggerNode?.closest<HTMLElement>(NESTED_POPUP_CONTAINERS) ?? document.body;

// antd's defaultRenderEmpty keeps these compact: they render inside a popup.
const COMPACT_EMPTY_COMPONENTS = ['Select', 'TreeSelect', 'Cascader', 'Transfer', 'Mentions'];

// `Table`/`List` arrive here whenever a call site passes neither `emptyMessageId` nor `emptyComponent`
// — `table.tsx` leaves `locale` empty and antd falls through to `renderEmpty('Table')`. They get the
// same composition the propped case wires, so there is only ever one in-table empty state.
export const renderDesignSystemEmpty = (componentName?: string) => {
  if (componentName && COMPACT_EMPTY_COMPONENTS.includes(componentName)) {
    return <CompactEmpty />;
  }

  return componentName === 'Table' || componentName === 'List' ? <TableEmptyState type="no-data" /> : <Empty />;
};

const AntdWrapper: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const intl = useIntl();
  const { appTheme } = useTheme();

  const theme = useMemo(() => getAntdTheme(appTheme), [appTheme]);

  const locale = useMemo<Locale>(
    () => ({
      ...antdLocaleUS,
      Table: {
        ...antdLocaleUS.Table,
        triggerAsc: intl.formatMessage({ id: 'sort.ascending' }),
        triggerDesc: intl.formatMessage({ id: 'sort.descending' }),
        cancelSort: intl.formatMessage({ id: 'sort.cancel' })
      }
    }),
    [intl]
  );
  return (
    <ConfigProvider
      csp={{ nonce: window.nonce }}
      theme={theme}
      locale={locale}
      getPopupContainer={getNestedPopupContainer}
      renderEmpty={renderDesignSystemEmpty}>
      <IconProvider value={{ csp: { nonce: window.nonce } }}>
        <Global styles={globalStyles} />
        <App>{children}</App>
      </IconProvider>
    </ConfigProvider>
  );
};

export default observer(AntdWrapper);

// The scrollbar is themed in `@env0/ui-theme/globals.css`, not here.
export const globalStyles = css`
  // Content and header padding share Collapse's \`padding\` token, so the theme config cannot zero one
  // without the other. !important because antd's own selector is the more specific.
  .ant-collapse-content-box {
    padding: 0 !important;
  }

  // antd gives .anticon line-height: 0 but not its clear-icon span, so at the Input block's
  // fontSizeIcon of 16 that span's text line box becomes the affix row height - 35px where the design
  // says 32. Global because the token is: it reaches every antd Input, including the raw ones
  // (popover-tree-selector) that never route through input-components/common-styles.ts.
  .ant-input-clear-icon {
    line-height: 0;
  }

  // antd 5.29 zeroes the line height of the svg inside a button's icon slot but not of the slot itself,
  // so .anticon's vertical-align: -0.125em adds 1.75px of descent under a 16px glyph and the icon rides
  // ~1px above the label. Global for the same reason as the clear icon: IconButton styles antd's raw
  // Button and never routes through common/button.tsx.
  .ant-btn-icon {
    line-height: 0;
  }
`;
